import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchLeads,
  updateLead,
  leadDate,
  type Lead,
  type LeadStatus,
} from "../services/leadsService";
import {
  fetchPipelines,
  fetchCategories,
  categoryClasses,
  type Pipeline,
  type PipelineCategory,
} from "../services/pipelinesService";
import { isLeadCallable } from "../services/leadPhone";
import { useSoftphone } from "../hooks/useSoftphone";
import SoftphonePanel from "../components/SoftphonePanel";
import CallLeadButton from "../components/CallLeadButton";
import LeadQualifications from "../components/LeadQualifications";
import { boardScrollbar } from "./boardScroll";
import { useTheme } from "../../../context/ThemeContext";

import { DIALOG_COLORS } from "../../../components/ui";
import {
  trackLeadStageMoved,
  trackLeadStageMoveAbandoned,
  type LeadMoveMethod,
} from "../../../analytics/crmAnalytics";
// ===========================================================================
// Pipeline (Kanban) — LIVE, lead-driven board. Columns are the selected
// pipeline's categories; cards are the leads sitting in each category. Dragging
// a lead to a column updates its category (and, for system columns, its status)
// via PUT /leads — the same endpoint the inbox uses.
//
// Lost drop-off: a lead in a "lost"-terminal column stays visible for 7 days
// (from its lostAt), then disappears from the board — it remains in the inbox.
// Only leads (crm_leads) ever appear here.
// ===========================================================================

const LOST_VISIBLE_MS = 7 * 24 * 60 * 60 * 1000;

/** A category is the lost-terminal kind subject to the 7-day drop-off. */
function isLostCategory(cat: PipelineCategory | undefined): boolean {
  return cat?.terminal === "lost";
}

/** ms since the lead entered its lost column, or null if unknown. */
function lostAgeMs(lead: Lead, now: number): number | null {
  const d = leadDate(lead.lostAt);
  return d ? now - d.getTime() : null;
}

function fmtDate(value: Lead["createdAt"]): string {
  const d = leadDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

const PRODUCT_TONE: Record<string, string> = {
  "Coding Cloud": "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  Wayak: "bg-indigo-500/15 text-indigo-500",
  Nuvex: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Movik: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
};

export default function PipelineBoard() {
  const tc = buildThemeClasses();
  // Sólo para la barra de desplazamiento del tablero (`boardScrollbar`).
  const { isDarkMode } = useTheme();

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [categories, setCategories] = useState<PipelineCategory[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCat, setOverCat] = useState<string | null>(null);
  const [detail, setDetail] = useState<Lead | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Browser softphone (human calls). Shared across all lead cards on the board.
  const softphone = useSoftphone();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pipes, cats, page] = await Promise.all([
        fetchPipelines(),
        fetchCategories(),
        fetchLeads({ limit: 500 }),
      ]);
      setPipelines(pipes);
      setCategories(cats);
      setLeads(page.data);
      setPipelineId((prev) => prev ?? pipes[0]?.id ?? null);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar el pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // After a human call ends, the backend transcribes + qualifies it (a minute or
  // two) and may move the lead to Contactado. Refresh shortly after so the card
  // lands in its new column without a manual reload.
  useEffect(() => {
    if (softphone.state.status !== "ended") return;
    const id = setTimeout(() => load(), 12000);
    return () => clearTimeout(id);
  }, [softphone.state.status, load]);

  // Global category lookup (covers categories beyond the active pipeline).
  const catById = useMemo(
    () => categories.reduce((acc, c) => ((acc[c.id] = c), acc), {} as Record<string, PipelineCategory>),
    [categories],
  );

  const pipeline = useMemo(
    () => pipelines.find((p) => p.id === pipelineId) ?? pipelines[0] ?? null,
    [pipelines, pipelineId],
  );

  // Columns = the pipeline's ordered categories (fall back to all categories).
  const columns = useMemo<PipelineCategory[]>(() => {
    if (pipeline && pipeline.categories.length) return pipeline.categories;
    return [...categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pipeline, categories]);

  // Leads on this board, search-filtered and past the 7-day lost cutoff.
  const visibleLeads = useMemo(() => {
    const now = Date.now();
    const q = query.trim().toLowerCase();
    const boardId = pipeline?.id;
    const boardProduct = pipeline?.product ? pipeline.product.trim().toLowerCase() : null;
    return leads.filter((l) => {
      // A lead belongs to this board when its pipelineId matches (legacy leads
      // with no pipelineId are treated as the default board's).
      const lp = l.pipelineId || "default";
      if (boardId && lp !== boardId) return false;
      // A product-scoped board only shows leads of that product.
      if (boardProduct && String(l.product || "").trim().toLowerCase() !== boardProduct) return false;
      // 7-day Perdido drop-off.
      const cat = catById[l.categoryId || l.status];
      if (isLostCategory(cat)) {
        const age = lostAgeMs(l, now);
        if (age !== null && age > LOST_VISIBLE_MS) return false;
      }
      if (!q) return true;
      return [l.name, l.company, l.email, l.product]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [leads, query, pipeline, catById]);

  // Bucket visible leads into their column (unmatched → first column).
  const byColumn = useMemo(() => {
    const map: Record<string, Lead[]> = {};
    for (const c of columns) map[c.id] = [];
    const fallback = columns[0]?.id;
    for (const l of visibleLeads) {
      const cid = l.categoryId || l.status;
      const target = map[cid] ? cid : fallback;
      if (target) map[target].push(l);
    }
    return map;
  }, [columns, visibleLeads]);

  const kpis = useMemo(() => {
    let open = 0;
    let won = 0;
    let lost = 0;
    for (const c of columns) {
      const n = byColumn[c.id]?.length ?? 0;
      if (c.terminal === "won") won += n;
      else if (c.terminal === "lost") lost += n;
      else open += n;
    }
    return { open, won, lost };
  }, [columns, byColumn]);

  const moveLead = useCallback(
    async (lead: Lead, category: PipelineCategory, method: LeadMoveMethod = "select") => {
      const boardId = pipeline?.id || lead.pipelineId || "default";
      const nextStatus = (category.statusKey as LeadStatus | null) ?? lead.status;
      const nowIso = new Date().toISOString();
      const nextLostAt = category.terminal === "lost" ? (lead.lostAt || nowIso) : null;

      setSavingId(lead.id);
      // Optimistic update (incl. lostAt so the 7-day filter reacts immediately).
      setLeads((prev) =>
        prev.map((l) =>
          l.id === lead.id
            ? { ...l, pipelineId: boardId, categoryId: category.id, status: nextStatus, lostAt: nextLostAt }
            : l,
        ),
      );
      setDetail((prev) =>
        prev && prev.id === lead.id
          ? { ...prev, pipelineId: boardId, categoryId: category.id, status: nextStatus, lostAt: nextLostAt }
          : prev,
      );
      try {
        await updateLead(lead.id, { pipelineId: boardId, categoryId: category.id });
        // `terminal` permite calcular la tasa de cierre sin tocar la base, y
        // `method` separa el arrastre del selector del detalle. El nombre del
        // lead y el motivo de perdida que teclea el vendedor nunca viajan.
        trackLeadStageMoved({
          fromStatus: lead.status ?? "unknown",
          toStatus: nextStatus ?? "unknown",
          terminal: category.terminal ?? "open",
          method,
        });
      } catch {
        await load();
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo mover el lead", confirmButtonColor: brand.primary });
      } finally {
        setSavingId(null);
      }
    },
    [pipeline, load],
  );

  const onDrop = (category: PipelineCategory) => {
    const id = dragId;
    setDragId(null);
    setOverCat(null);
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || (lead.categoryId || lead.status) === category.id) return;
    if (category.terminal === "lost") {
      void confirmLost(lead, category, "drag");
    } else {
      void moveLead(lead, category, "drag");
    }
  };

  const confirmLost = async (
    lead: Lead,
    category: PipelineCategory,
    method: LeadMoveMethod = "select",
  ) => {
    const { isConfirmed } = await Swal.fire({
      title: "Marcar como perdido",
      input: "text",
      inputLabel: `¿Por qué se perdió el lead de ${lead.name ?? "—"}? (opcional)`,
      inputPlaceholder: "Precio, competidor, sin presupuesto…",
      showCancelButton: true,
      confirmButtonText: "Marcar perdido",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!isConfirmed) {
      // Distingue el arrastre accidental hacia la columna terminal de una
      // perdida real, que es justo lo que hoy ensucia el reporte de cierre.
      trackLeadStageMoveAbandoned(
        (category.statusKey as string | null) ?? "lost",
      );
      return;
    }
    await moveLead(lead, category, method);
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden p-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-layout-column-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Pipeline</h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              Arrastra los leads entre etapas para actualizar su estado
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {pipelines.length > 0 && (
            <select
              value={pipeline?.id ?? ""}
              onChange={(e) => setPipelineId(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content"
              title="Cambiar de pipeline"
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.product ? ` · ${p.product}` : ""}</option>
              ))}
            </select>
          )}
          {pipeline?.product && (
            <span className="inline-flex items-center gap-1 rounded-lg bg-slate-500/15 px-2.5 py-2 text-xs font-medium text-slate-500" title="Este tablero filtra por producto">
              <i className="ri-price-tag-3-line" />{pipeline.product}
            </span>
          )}
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar leads..."
              className={`w-40 bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>
          <button
            onClick={load}
            title="Actualizar desde el servidor"
            className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <Kpi tc={tc} label="Leads activos" value={String(kpis.open)} icon="ri-folder-open-line" accent />
        <Kpi tc={tc} label="Convertidos" value={String(kpis.won)} icon="ri-trophy-line" />
        <Kpi tc={tc} label="Perdidos (7 días)" value={String(kpis.lost)} icon="ri-close-circle-line" />
      </div>

      {/* Board */}
      {loading ? (
        <div className={`flex-1 text-center text-sm ${tc.textSecondary} p-10`}>
          <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando pipeline...
        </div>
      ) : error ? (
        <div className="flex-1 p-10 text-center">
          <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
            Reintentar
          </button>
        </div>
      ) : columns.length === 0 ? (
        <div className={`flex-1 p-10 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-inbox-line mb-2 block text-3xl" />
          Este pipeline no tiene categorías. Agrégalas en Configuración → Pipelines.
        </div>
      ) : (
        <div className={`flex min-h-0 min-w-0 flex-1 gap-3 overflow-x-auto pb-2 ${boardScrollbar(isDarkMode)}`}>
          {columns.map((cat) => {
            const items = byColumn[cat.id] ?? [];
            const cc = categoryClasses(cat.color);
            const isOver = overCat === cat.id;
            return (
              <div
                key={cat.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (overCat !== cat.id) setOverCat(cat.id);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverCat(null);
                }}
                onDrop={() => onDrop(cat)}
                className={`flex w-72 flex-shrink-0 flex-col rounded-xl border ${
                  isOver ? "border-primary-500 ring-2 ring-primary-500/30" : tc.cardBorder
                } bg-app`}
              >
                {/* Column header */}
                <div className={`sticky top-0 rounded-t-xl border-b p-3 ${tc.cardBorder} ${tc.cardBg}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${cc.dot}`} />
                      <span className={`text-sm font-semibold ${tc.text}`}>{cat.name}</span>
                      <span className="rounded-full px-1.5 text-xs bg-subtle text-content-muted">
                        {items.length}
                      </span>
                    </div>
                    {cat.terminal !== "open" && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cc.tone}`}>
                        {cat.terminal === "won" ? "Cierre" : "Perdido"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Cards */}
                <div className={`flex flex-1 flex-col gap-2 overflow-y-auto p-2 ${boardScrollbar(isDarkMode)}`}>
                  {items.length === 0 ? (
                    <div className={`rounded-lg border border-dashed py-6 text-center text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
                      Suelta un lead aquí
                    </div>
                  ) : (
                    items.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        tc={tc}
                        accent={cc.dot}
                        dragging={dragId === lead.id}
                        saving={savingId === lead.id}
                        // Show the call button on "Nuevo"-column leads that have a
                        // phone — either a WhatsApp line or a plain `phone`, which
                        // is what the backend dials too.
                        callable={cat.statusKey === "new" && isLeadCallable(lead)}
                        onStartHuman={(l) => softphone.startCall(l)}
                        onDragStart={() => setDragId(lead.id)}
                        onDragEnd={() => {
                          setDragId(null);
                          setOverCat(null);
                        }}
                        onClick={() => setDetail(lead)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className={`mt-2 text-xs ${tc.textSecondary}`}>
        <i className="ri-information-line mr-1" />
        Datos en vivo. Cada lead nuevo entra en “Nuevo”. Los leads marcados como Perdido se ocultan del tablero tras 7 días (siguen en la bandeja).
      </p>

      {/* Detail modal */}
      {detail && (
        <Modal
          title={`Lead — ${detail.name ?? "—"}`}
          onClose={() => setDetail(null)}
          themeClasses={tc}
          content={
            <LeadDetail
              lead={detail}
              tc={tc}
              columns={columns}
              catById={catById}
              onMove={(cat) => {
                if (cat.terminal === "lost") void confirmLost(detail, cat);
                else void moveLead(detail, cat);
              }}
              onQualified={(updated) => {
                setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
                setDetail((prev) => (prev && prev.id === updated.id ? updated : prev));
              }}
            />
          }
        />
      )}

      {/* Floating browser softphone (human calls) */}
      <SoftphonePanel
        state={softphone.state}
        onHangup={softphone.hangup}
        onToggleMute={softphone.toggleMute}
        onClose={softphone.reset}
      />
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

type TC = ReturnType<typeof buildThemeClasses>;

function Kpi({ tc, label, value, icon, accent }: { tc: TC; label: string; value: string; icon: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className={`text-xs ${tc.textSecondary}`}>{label}</span>
        <i className={`${icon} ${accent ? "text-primary-500" : tc.textSecondary}`} />
      </div>
      <div className={`text-lg font-bold ${accent ? "text-primary-500" : tc.text}`}>{value}</div>
    </div>
  );
}

function LeadCard({
  lead,
  tc,
  accent,
  dragging,
  saving,
  callable,
  onStartHuman,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  lead: Lead;
  tc: TC;
  accent: string;
  dragging: boolean;
  saving: boolean;
  callable: boolean;
  onStartHuman: (lead: Lead) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  const productTone = PRODUCT_TONE[String(lead.product)] ?? "bg-gray-400/15 text-gray-500";
  return (
    <div
      draggable={!saving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`relative cursor-grab rounded-lg border p-3 shadow-sm transition active:cursor-grabbing ${
        tc.cardBorder
      } bg-surface ${dragging ? "opacity-40" : "hover:border-primary-500/60"} ${
        saving ? "pointer-events-none opacity-60" : ""
      }`}
    >
      {/* Call button — floats over Nuevo leads so a rep can dial straight away. */}
      {callable && (
        <div className="absolute right-2 top-2">
          <CallLeadButton lead={lead} tc={tc} onStartHuman={onStartHuman} />
        </div>
      )}

      <div className={`mb-1 flex items-start gap-2 ${callable ? "pr-16" : ""}`}>
        <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${accent}`} />
        <p className={`text-sm font-semibold leading-tight ${tc.text}`}>{lead.name ?? "—"}</p>
      </div>
      {lead.company && (
        <p className={`mb-2 truncate text-xs ${tc.textSecondary}`}>
          <i className="ri-building-line mr-1" />{lead.company}
        </p>
      )}
      <div className="flex items-center justify-between">
        {lead.product ? (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${productTone}`}>
            {String(lead.product)}
          </span>
        ) : <span />}
        <span className={`text-[11px] ${tc.textSecondary}`}>
          <i className="ri-calendar-line mr-1" />{fmtDate(lead.createdAt)}
        </span>
      </div>
    </div>
  );
}

function LeadDetail({
  lead,
  tc,
  columns,
  catById,
  onMove,
  onQualified,
}: {
  lead: Lead;
  tc: TC;
  columns: PipelineCategory[];
  catById: Record<string, PipelineCategory>;
  onMove: (cat: PipelineCategory) => void;
  onQualified: (lead: Lead) => void;
}) {
  const cat = catById[lead.categoryId || lead.status];
  const cc = categoryClasses(cat?.color);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${cc.tone}`}>
          <span className={`h-2 w-2 rounded-full ${cc.dot}`} /> {cat?.name ?? lead.status}
        </span>
        {lead.product && (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${PRODUCT_TONE[String(lead.product)] ?? "bg-gray-400/15 text-gray-500"}`}>
            {String(lead.product)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field tc={tc} label="Empresa" value={lead.company} />
        <Field tc={tc} label="Correo" value={lead.email} />
        <Field tc={tc} label="WhatsApp" value={lead.whatsapp} />
        <Field tc={tc} label="Teléfono" value={lead.phone} />
        <Field tc={tc} label="Sistema de interés" value={lead.system} />
        <Field tc={tc} label="Origen" value={lead.source} />
        <Field tc={tc} label="Recibido" value={fmtDate(lead.createdAt)} />
      </div>

      {/* Dual qualification — the AI grade and the assigned agent's grade. */}
      <LeadQualifications lead={lead} tc={tc} onQualified={onQualified} />

      <div className={`border-t pt-3 ${tc.border}`}>
        <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Mover a etapa</label>
        <select
          value={lead.categoryId || lead.status}
          onChange={(e) => {
            const next = columns.find((c) => c.id === e.target.value);
            if (next) onMove(next);
          }}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content"
        >
          {columns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        {lead.whatsapp && (
          <a
            href={`https://wa.me/${lead.whatsapp.replace(/[^\d]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-emerald-500 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-500/10"
          >
            <i className="ri-whatsapp-line mr-1" /> WhatsApp
          </a>
        )}
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="rounded-lg border px-4 py-2 text-sm hover:bg-primary-500/10">
            <i className="ri-mail-line mr-1" /> Correo
          </a>
        )}
      </div>
    </div>
  );
}

function Field({ tc, label, value }: { tc: TC; label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}
